import { useEffect, useState } from "react";
import { CandlestickChart, Layers, Receipt, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { authService } from "@/services/authService";
import { AppShell } from "@/components/AppShell";
import { fmtMoney } from "@/lib/format";
import { USER_NAV as NAV } from "@/pages/userNav";
import { useLanguage } from "@/context/LanguageContext";
import { TerminalQrCard } from "@/components/TerminalQrCard";

const Stat = ({ label, value, icon: Icon, testId, mono = true }) => (
  <div
    data-testid={testId}
    className="rounded-md border border-[#22252F] bg-[#12141A] p-6 transition-colors hover:border-[#2563EB]/40"
  >
    <div className="flex items-center justify-between">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">{label}</p>
      <Icon className="h-4 w-4 text-[#60A5FA]" />
    </div>
    <p className={`mt-6 text-2xl text-white ${mono ? "font-mono" : "font-heading"}`}>{value}</p>
  </div>
);

export default function DashboardPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    authService.overview().then(setData).catch(() => setData(null));
  }, []);

  const p = data?.portfolio;

  return (
    <AppShell nav={NAV} title={`${t("dash.hello")}, ${user?.name?.split(" ")[0] || ""}`} subtitle={t("dash.subtitle")}>
      <div data-testid="user-dashboard" className="space-y-10">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Stat testId="stat-balance" label={t("dash.balance")} icon={Wallet}
                value={fmtMoney(p?.cash_balance ?? user?.virtual_balance, p?.currency || "USD")} />
          <Stat testId="stat-invested" label={t("dash.invested")} icon={CandlestickChart}
                value={fmtMoney(p?.invested_value, p?.currency || "USD")} />
          <Stat testId="stat-positions" label={t("dash.positions")} icon={Layers} value={data?.positions_count ?? 0} />
          <Stat testId="stat-trades" label={t("dash.trades")} icon={Receipt} value={data?.trades_count ?? 0} />
        </div>

        <section className="rounded-md border border-[#22252F] bg-[#12141A] p-8 sm:p-10">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] text-[#60A5FA]">{t("dash.kicker")}</p>
              <h2 className="mt-4 font-heading text-lg font-light tracking-tight text-white md:text-lg">
                {t("dash.title")}
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#94A3B8]">
                {t("dash.text")}
              </p>
              <a
                href="/terminal"
                data-testid="go-to-terminal"
                className="mt-8 inline-flex rounded-md bg-[#2563EB] px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#1D4ED8]"
              >
                {t("dash.openTerminal")}
              </a>
              <div className="mt-8 grid grid-cols-2 gap-3 font-mono text-xs text-[#64748B] sm:grid-cols-5">
                {["CRYPTO", "STOCKS", "FOREX", "COMMODITIES", "INDICES"].map((m) => (
                  <span key={m} className="border border-[#22252F] px-3 py-2 text-center">{m}</span>
                ))}
              </div>
            </div>

            <TerminalQrCard />
          </div>
        </section>
      </div>
    </AppShell>
  );
}
